import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { LabeledOption } from '@e3dc-react/shell/Types/globalTypes'
import {
  ApplicationArea,
  ApplicationAreas,
  ConnectionType,
  ConnectionTypes,
  InstallationType,
  InstallationTypes,
  NetConfiguration,
  NetConfigurations,
  WeatherCondition,
  WeatherConditions,
} from '../types'

interface InstallationStatementLabels {
  ConnectionTypeOptions: LabeledOption<ConnectionType>[]
  WeatherConditionOptions: LabeledOption<WeatherCondition>[]
  NetConfigurationOptions: LabeledOption<NetConfiguration>[]
  InstallationTypeOptions: LabeledOption<InstallationType>[]
  ApplicationAreaOptions: LabeledOption<ApplicationArea>[]
  WallMountedOptions: LabeledOption<boolean>[]
  EmergencyOptions: LabeledOption<boolean>[]
}

/**
 * This hook provides labeled storage options for the installation statement
 * Labeled options are used for select fields.
 */
const useLabeledStorageOptions = (): InstallationStatementLabels => {
  const { t } = useCustomTranslation()

  const ConnectionTypeOptions: LabeledOption<ConnectionType>[] = [
    {
      label: t('installation-statement.storage.label.connection-type.ac'),
      value: ConnectionTypes.Ac,
    },
    {
      label: t('installation-statement.storage.label.connection-type.dc'),
      value: ConnectionTypes.Dc,
    },
    {
      label: t('installation-statement.storage.label.connection-type.hybrid'),
      value: ConnectionTypes.Hybrid,
    },
  ]

  const WeatherConditionOptions: LabeledOption<WeatherCondition>[] = [
    {
      label: t('installation-statement.storage.label.weather-conditions.clear'),
      value: WeatherConditions.Clear,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.clouds'),
      value: WeatherConditions.Clouds,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.drizzle'),
      value: WeatherConditions.Drizzle,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.rain'),
      value: WeatherConditions.Rain,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.thunderstorm'),
      value: WeatherConditions.Thunderstorm,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.snow'),
      value: WeatherConditions.Snow,
    },
    {
      label: t('installation-statement.storage.label.weather-conditions.mist'),
      value: WeatherConditions.Mist,
    },
  ]

  const NetConfigurationOptions: LabeledOption<NetConfiguration>[] = [
    {
      label: t('installation-statement.storage.label.net-configuration.tn-c'),
      value: NetConfigurations.Tnc,
    },
    {
      label: t('installation-statement.storage.label.net-configuration.tn-s'),
      value: NetConfigurations.Tns,
    },
    {
      label: t('installation-statement.storage.label.net-configuration.tn-c-s'),
      value: NetConfigurations.Tncs,
    },
    {
      label: t('installation-statement.storage.label.net-configuration.tt'),
      value: NetConfigurations.Tt,
    },
    {
      label: t('installation-statement.storage.label.net-configuration.it'),
      value: NetConfigurations.It,
    },
  ]

  const InstallationTypeOptions: LabeledOption<InstallationType>[] = [
    {
      label: t('installation-statement.storage.label.installation-type.looped-through'),
      value: InstallationTypes.LoopedThrough,
    },
    {
      label: t('installation-statement.storage.label.installation-type.stitch-connected'),
      value: InstallationTypes.StitchConnected,
    },
  ]

  const ApplicationAreaOptions: LabeledOption<ApplicationArea>[] = [
    {
      label: t('installation-statement.storage.label.application-area.private'),
      value: ApplicationAreas.Private,
    },
    {
      label: t('installation-statement.storage.label.application-area.commercial'),
      value: ApplicationAreas.Commercial,
    },
  ]

  const WallMountedOptions: LabeledOption<boolean>[] = [
    {
      label: t('installation-statement.storage.label.wall-mounted.yes'),
      value: true,
    },
    {
      label: t('installation-statement.storage.label.wall-mounted.no'),
      value: false,
    },
  ]

  const EmergencyOptions: LabeledOption<boolean>[] = [
    {
      label: t('installation-statement.storage.label.emergency-option.yes'),
      value: true,
    },
    {
      label: t('installation-statement.storage.label.emergency-option.no'),
      value: false,
    },
  ]

  return {
    ConnectionTypeOptions,
    WeatherConditionOptions,
    NetConfigurationOptions,
    InstallationTypeOptions,
    ApplicationAreaOptions,
    WallMountedOptions,
    EmergencyOptions,
  }
}

export default useLabeledStorageOptions
